import { Power0, TweenMax } from 'gsap';
import { Container, Rectangle, Sprite, Text } from 'pixi.js';
import Core from '../../../Core/Core';
import {
    TGameAreas,
    TransactionActionTypes,
    TransactionTypes,
} from '../../../DeltaTracking/ResourceParamBuilder';
import { HudActions, HudEmitter } from '../../../Managers/Hud/HudEmitter';
import SoundManager from '../../../Sound/SoundManager';
import { Actions } from '../../../State/Actions/Actions';
import { hasAlreadyBeenSentCoinsTo } from '../../../State/reducers/social';
import DateUtils from '../../../Utils/DateUtils';
import { createOutlineText, createPixiText, getRandom } from '../../../Utils/Utils';
import { TileGoldMatch } from '../../Animations/TileGoldMatch';
import SocialTracking from '../../../Managers/Tracking/SocialTracking';
import ResourceTracking from '../../../Managers/Tracking/ResourceTracking';

export class SocialSendCoinsBtn extends Container {
    private dragging: boolean = false;
    private readonly friendId: string;
    private readonly rays: Sprite;
    private readonly coins: Sprite;
    private readonly timer: Text;
    private interval: any;
    private readonly coolDown: number;
    private readonly canSendGifts: boolean;

    constructor(friendId: string, canSendGifts: boolean) {
        super();

        this.friendId = friendId;

        this.rays = new PIXI.Sprite(PIXI.utils.TextureCache['commonui/opt-in_icon_rays']);
        this.rays.anchor.set(0.5);
        this.rays.scale.set(0.55);
        this.rays.x = 2;
        this.addChild(this.rays);

        this.coins = new PIXI.Sprite(PIXI.utils.TextureCache['commonui/leaderboard-reward-coins']);
        this.coins.anchor.set(0.5);
        this.addChild(this.coins);

        this.timer = createPixiText(
            '00:00:00',
            new Rectangle(0, 30, 100, 10),
            'Baloo',
            22,
            28,
            0x006eba
        );
        this.addChild(this.timer);

        this.canSendGifts = canSendGifts;
        this.coolDown = DateUtils.hoursToMilliseconds(canSendGifts ? 8 : 72);

        this.updateTimer(this.coolDown);

        this.enable();
        this.on('pointerup', this.handlePointerDown.bind(this));
        HudEmitter.instance.on(HudActions.SOCIALHUB_STOP_LOAD_SOCIAL, this.updateUi.bind(this));
        this.updateUi();
    }

    /**
     * Stop the count down and kill any rays animation.
     */
    public destroy(options?: {
        children?: boolean;
        texture?: boolean;
        baseTexture?: boolean;
    }): void {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
        TweenMax.killTweensOf(this.rays);
        super.destroy(options);
    }

    public setDraggin(bool: boolean) {
        this.dragging = bool;
    }

    /**
     * Set as interactable, show coins, hide timer and start rays animation.
     */
    private enable(): void {
        this.interactive = true;
        this.buttonMode = true;

        this.coins.alpha = 1;
        this.timer.visible = false;

        this.rays.visible = true;
        this.rays.rotation = 0;
        TweenMax.to(this.rays, 5, {
            rotation: 2 * Math.PI, // 360
            repeat: -1,
            ease: Power0.easeNone,
        });
    }

    /**
     * Set as non-interactable, hide coins, show timer and kill rays animation.
     */
    private disable(): void {
        this.interactive = false;
        this.buttonMode = false;

        this.coins.alpha = 0.3;
        this.timer.visible = true;

        this.rays.visible = false;
        TweenMax.killTweensOf(this.rays);
    }

    private async handlePointerDown(): Promise<void> {
        if (!this.dragging) {
            this.disable();

            HudEmitter.instance.emit(HudActions.SOCIALHUB_START_LOAD_SOCIAL);

            const coins = getRandom(15, 25);

            SocialTracking.clickCoins();

            const result = this.canSendGifts
                ? await Core.sdk.messages.sendCoins(this.friendId, coins)
                : true;
            if (result) {
                Core.store.dispatch(Actions.sendCoins(this.friendId));
            }
            HudEmitter.instance.emit(HudActions.SOCIALHUB_STOP_LOAD_SOCIAL);

            if (result) {
                const sentCoins = hasAlreadyBeenSentCoinsTo(
                    Core.store.getState().StateSocial,
                    this.friendId
                );
                if (sentCoins) {
                    this.playCoinAnimation(coins);
                    Core.store.dispatch(Actions.updateCoins(coins));
                    // TODO what event to track...resource, social, or both?
                    // Can I use daily mission action type?...or another one for coins?...
                    ResourceTracking.sendCoinsResourceEvent(
                        coins,
                        TransactionTypes.REWARD,
                        TransactionActionTypes.DAILY_MISSION,
                        TGameAreas.MAP
                    );
                }
            } else {
                this.enable();
            }
        }
    }

    private updateUi() {
        const sentCoins = hasAlreadyBeenSentCoinsTo(
            Core.store.getState().StateSocial,
            this.friendId
        );
        if (sentCoins) {
            const timeRemaining = DateUtils.timeRemainingTo(this.coolDown + sentCoins.ts);
            if (timeRemaining > 0) {
                this.disable();
                this.startCountdown(sentCoins.ts);
            } else {
                if (sentCoins.ts !== 0) {
                    Core.store.dispatch(Actions.resetSendCoins(sentCoins.friendId));
                }
            }
        }
    }

    private startCountdown(ts: number): void {
        this.updateTimer(DateUtils.timeRemainingTo(this.coolDown + ts));

        this.interval = setInterval(() => {
            const timeRemaining = DateUtils.timeRemainingTo(this.coolDown + ts);
            if (timeRemaining < 0) {
                clearInterval(this.interval);
                this.interval = null;
                this.enable();
            } else {
                this.updateTimer(timeRemaining);
            }
        }, 1000);
    }

    private updateTimer(timeRemaining: number): void {
        this.timer.text = DateUtils.format(timeRemaining, 'hh:mm:ss');
    }

    private playCoinAnimation(coins: number): void {
        const anim = new TileGoldMatch();
        anim.position.set(6, 15);
        this.addChild(anim);

        SoundManager.playSound('GameCoin');

        const text = createOutlineText(
            `+${coins}\n${Core.local.getText('coins')}`,
            new PIXI.Rectangle(0, 15, 100, 50),
            'Baloo',
            24,
            24,
            0xffffff,
            0x006eba,
            4
        );
        this.addChild(text);

        TweenMax.to(text, 0.5, {
            y: 5,
            onComplete: () => {
                this.removeChild(text);
                text.destroy();
            },
        });
    }
}
